import React, { useState, useEffect } from 'react'
import {
  Building2, Calendar, FileText, Tag, ExternalLink,
} from 'lucide-react'
import Modal from '../ui/Modal'
import ContractDetailModal from './ContractDetailModal'
import { type ContractDetail, num, fmtShort, fmtDate, usaspendingUrl } from './AwardAmountsChart'
import { tokenManager } from '../../lib/api'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface VendorContractsModalProps {
  isOpen: boolean
  onClose: () => void
  vendorId: number
  vendorName: string
}

/**
 * Compact inline bar showing obligated vs. potential amounts for a contract row.
 */
const MiniAmountBar: React.FC<{ contract: ContractDetail }> = ({ contract }) => {
  const obligated = num(contract.contract_value || contract.total_account_obligation)
  const potential = num(contract.base_and_all_options)
  const outlayed = num(contract.total_outlays || contract.total_account_outlay)

  const maxVal = Math.max(obligated, potential, 1)
  if (obligated === 0 && potential === 0) return <span className="text-xs text-muted-foreground">-</span>

  return (
    <div className="space-y-1 min-w-[140px]">
      <div className="relative h-3 bg-muted rounded-full overflow-hidden">
        {potential > 0 && (
          <div
            className="absolute inset-y-0 left-0 bg-blue-200/30 dark:bg-blue-200/10"
            style={{ width: '100%' }}
          />
        )}
        {obligated > 0 && (
          <div
            className="absolute inset-y-0 left-0 bg-blue-600/60 dark:bg-blue-600/40 rounded-full"
            style={{ width: `${(obligated / maxVal) * 100}%` }}
          />
        )}
        {outlayed > 0 && (
          <div
            className="absolute inset-y-0 left-0 bg-blue-900 dark:bg-blue-800 rounded-full"
            style={{ width: `${(outlayed / maxVal) * 100}%` }}
          />
        )}
      </div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>{fmtShort(obligated)} obligated</span>
        {potential > 0 && potential !== obligated && (
          <span>{fmtShort(potential)} max</span>
        )}
      </div>
    </div>
  )
}

const VendorContractsModal: React.FC<VendorContractsModalProps> = ({
  isOpen,
  onClose,
  vendorId,
  vendorName,
}) => {
  const [contracts, setContracts] = useState<ContractDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedContract, setSelectedContract] = useState<ContractDetail | null>(null)

  useEffect(() => {
    if (!isOpen) return

    const loadContracts = async () => {
      setLoading(true)
      setError(null)
      const token = tokenManager.getAccessToken()

      try {
        const res = await fetch(`${API_BASE_URL}/api/vendors/${vendorId}/contracts/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok) {
          const data = await res.json()
          setContracts(data.results || data || [])
        } else if (res.status === 401) {
          // The access token expired. This call does not go through apiRequest,
          // so nothing refreshes it and nothing tells the user (DEV-663).
          setError('Your session has expired. Sign in again to load contracts.')
        } else if (res.status === 403 || res.status === 404) {
          // Not "no contracts": the vendor itself is not readable by this user.
          // VendorViewSet filters get_object() through the visibility queryset,
          // so a denied vendor 404s exactly like a missing one (DEV-669).
          setError('This vendor is not available to your account.')
        } else {
          setError(`Failed to load contracts (HTTP ${res.status}).`)
        }
      } catch {
        setError('Could not reach the server to load contracts.')
      } finally {
        setLoading(false)
      }
    }

    loadContracts()
  }, [isOpen, vendorId])

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-blue-500" />
          <span>Contracts &mdash; {vendorName}</span>
          {!loading && contracts.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground">
              ({contracts.length} contract{contracts.length !== 1 ? 's' : ''})
            </span>
          )}
        </div>
      } size="xl">
        <div className="max-h-[70vh] overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3" />
              <p className="text-sm text-muted-foreground">Loading contracts...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          ) : contracts.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                No contracts found for this vendor.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {contracts.map((contract) => (
                <button
                  key={contract.id}
                  onClick={() => setSelectedContract(contract)}
                  className="w-full border border-border rounded-lg p-4 text-left
                             hover:border-blue-500/40 hover:bg-blue-500/5 transition-all cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    {/* Left: contract info */}
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-semibold text-foreground truncate">
                          {contract.contract_number}
                        </span>
                        {contract.type_description && (
                          <span className="px-1.5 py-0 rounded text-[9px] font-medium bg-muted text-muted-foreground flex-shrink-0">
                            {contract.type_description}
                          </span>
                        )}
                      </div>

                      {contract.title && (
                        <p className="text-xs text-foreground line-clamp-1">{contract.title}</p>
                      )}

                      <div className="flex items-center gap-3 flex-wrap">
                        {contract.awarding_agency && (
                          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <Building2 className="w-3 h-3" />
                            {contract.awarding_agency}
                          </span>
                        )}
                        {(contract as ContractDetail & { product_name?: string }).product_name && (
                          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <Tag className="w-3 h-3" />
                            {(contract as ContractDetail & { product_name?: string }).product_name}
                          </span>
                        )}
                        {contract.award_date && (
                          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            {fmtDate(contract.award_date)}
                          </span>
                        )}
                        {usaspendingUrl(contract) && (
                          <a
                            href={usaspendingUrl(contract)!}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-[10px] text-primary hover:text-primary/80"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="w-3 h-3" />
                            USAspending
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Right: mini bar */}
                    <div className="flex-shrink-0">
                      <MiniAmountBar contract={contract} />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Contract Detail Modal (nested) */}
      {selectedContract && (
        <ContractDetailModal
          isOpen={!!selectedContract}
          onClose={() => setSelectedContract(null)}
          contract={selectedContract}
        />
      )}
    </>
  )
}

export default VendorContractsModal
